import React from 'react';
import { Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import styled  from 'styled-components';
import config from '../../../../config/channels';
import { TChannel } from '../../../../contexts/operator/operator-context';
import useOperatorContext from '../../../../contexts/operator/useOperatorContext';
import NavBadge from '../../../general/NavBadge';
import { trackSiteSelectTab } from '../../../../services/siteSelectTracking';

type ChannelSelectorProps = {
    children: JSX.Element | JSX.Element[];
    enabledChannels: TChannel[];
    selectedChannel: string;
    setActiveChannelTab: React.Dispatch<React.SetStateAction<string>>;
};

const ChannelSelector = ({
    children,
    selectedChannel,
    enabledChannels,
    setActiveChannelTab
}: ChannelSelectorProps) => {
    const navigate = useNavigate();
    const operatorContext = useOperatorContext();

    const renderChannelNav = (channel: TChannel, selected: string, enabledChannels: number) => {
        let displayName = config[channel]['displayName'];

        if ( channel === 'catering' ) {
            displayName = operatorContext.operator?.theme.v4.catering_label ?? 'Catering';
        }

        if ( channel === 'venues' ) {
           // Override the default display name of "delivery" which is used everywhere else
           displayName = config['venues']['displayName'];
        }

        return (
            <NavBadge size={'md'} key={config[channel]['handle']}>
                <Nav.Link
                    className={`${selected === channel ? 'active' : undefined} ${enabledChannels === 1 ? 'nav-link--solo' : ''}`}
                    eventKey={`/${config[channel]['handle']}`}
                    onClick={() => selectChannel(channel)}
                >
                    { displayName }
                </Nav.Link>
            </NavBadge>
        );
    };

    const selectChannel = (channel: TChannel) => {
        trackSiteSelectTab('page', channel);
        navigate(`/` + config[channel]['handle']);
    };

    return (
        <ChannelSelectWrapper>
            <StyledContainer className={'channel-select-container'}>
                { operatorContext?.operator?.theme?.v4?.slogan && (
                  <Heading className="slogan text-left"
                           dangerouslySetInnerHTML={{ __html: operatorContext?.operator?.theme.v4.slogan ?? '' }} />
                )}
                <StyledNavContainer>
                  <StyledNav
                     soloChannel={enabledChannels.length === 1}
                     className="channel-select-nav"
                     variant="buttons"
                     defaultActiveKey={`/${selectedChannel}`}
                  >
                     { enabledChannels.map((item, index) => {
                           return renderChannelNav(item, selectedChannel, enabledChannels.length)
                     })}
                  </StyledNav>
                  <Shadow/>
                </StyledNavContainer>
                <>{children}</>
            </StyledContainer>
        </ChannelSelectWrapper>
    );
};

const ChannelSelectWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 60px;
    padding-bottom: 32px;
    overscroll-behavior-y: none;

    @media screen and (max-width: 767px){
        width: 100%;
        margin-top: 0;
    }

    @media screen and (min-width: 768px){
        min-height: 100vh;
    }
`;

const StyledContainer = styled.div`
    width: 100%;
`;

const StyledNavContainer = styled.div`
   position: relative;
`;

const StyledNav = styled(({ soloChannel, ...props }) => <Nav {...props} />)`
    padding: 0;
    top: 0;
    z-index: 1;
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    margin: 24px 60px 0 60px;

    @media screen and (max-width: 767px){
        margin: 24px 0 0 0;
    }

   &.nav-lines {
        display: flex;
    }

     &.nav-buttons {
        padding-bottom: 0;

        @media screen and (max-width: 767px) {
            .nav-item:first-child {
                margin-left: 16px;
            }

            .nav-items:last-child {
               margin-right: 16px;
            }
        }
    }

    ${(props) =>
        props.soloChannel
            ? null
            : `@media screen and (max-width: 767px) {
            overflow: auto visible;
            scrollbar-width: none;
            scroll-behavior: smooth;
            display: block;
            white-space: nowrap;
        }`}

    @media screen and (max-width: 767px) {
        position: relative !important;
        z-index: 1 !important;
        display: flex;
    }
`;

const Shadow = styled.div`
   @media screen and (max-width: 767px) {
      position: absolute;
      right: 0;
      top: 0;
      bottom:0;
      width: 32px;
      background: linear-gradient(90deg, var(--layouts_nav_bg_opa0), var(--layouts_nav_bg));
      z-index: 2;
   }
`;

const Heading = styled.h1`
    margin: 24px 0;
    padding: 0 60px;
    font-size: 32px;
    font-weight: bold;
    line-height: 40px;
    text-wrap: pretty;

    @media screen and (max-width: 767px) {
        padding: 0 16px;
    }
`;

export default ChannelSelector;
